import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Loader2, Save, Shield, EyeOff, Eye, Smartphone, Trash2,
  KeyRound, LogOut, RefreshCw, AlertTriangle, Check,
} from 'lucide-react';
import { authApi, deviceApi, DEVICE_FP_KEY } from '@/services/api.js';
import { useAuth } from '@/context/AuthContext.jsx';
import PageHeader from '@/components/common/PageHeader.jsx';
import StatusBadge from '@/components/common/StatusBadge.jsx';
import { Card, CardHeader, EmptyState, ErrorPanel } from '@/components/common/Card.jsx';
import { fmtDateTime, fmtRelative, fmtRoleLabel } from '@/utils/format.js';

/*
|------------------------------------------------------------------------------
| Account settings
|------------------------------------------------------------------------------
| The self-service surface every signed-in role shares: profile, privacy mode,
| what your roles actually entitle you to, your registered devices, and
| session control.
|
| Two of these are consequential rather than cosmetic and are presented that
| way:
|
|   Privacy mode  — `anonymous` vs `identified` governs whether reports you
|                   file carry attributable submitter identity. It is not a
|                   display preference.
|
|   Device revoke — devices gate access. Revoking the browser you are sitting
|                   in will lock you out of report submission and the Vision
|                   consent pages until you sign in again, so this device is
|                   labelled and confirmed separately.
*/

export default function AccountSettings() {
  const { t } = useTranslation();
  const { user, roles } = useAuth();

  return (
    <>
      <PageHeader
        title={t('home.accountSettings.account')}
        description={t('home.accountSettings.yourProfilePrivacyModeRegisteredDevices')}
      />

      <div className="grid gap-6 lg:grid-cols-3 lg:items-start">
        <div className="space-y-6 lg:col-span-2">
          <ProfileCard user={user} />
          <PrivacyModeCard user={user} />
          <DevicesCard />
        </div>

        <div className="space-y-6">
          <RolesCard roles={roles} />
          <SessionsCard />
        </div>
      </div>
    </>
  );
}

// ── Profile ──────────────────────────────────────────────────────────────────

function ProfileCard({ user }) {
  const { t } = useTranslation();
  const { applyProfile } = useAuth();
  const [form, setForm] = useState({
    name:  user?.name  ?? '',
    email: user?.email ?? '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const dirty = form.name !== (user?.name ?? '') || form.email !== (user?.email ?? '');

  const save = useMutation({
    mutationFn: () => authApi.updateProfile({
      name:  form.name.trim() || null,
      email: form.email.trim() || null,
    }),
    onSuccess: (res) => {
      toast.success('Profile updated.');
      applyProfile({ name: res?.name ?? null, email: res?.email ?? null });
    },
    onError: err => {
      const errors = err?.response?.data?.errors;
      const first  = errors && Object.values(errors)[0]?.[0];
      toast.error(first || err?.response?.data?.message || 'Could not save your profile.');
    },
  });

  return (
    <Card>
      <CardHeader title={t('home.accountSettings.profile')} description={t('home.accountSettings.howYouAppearToOperatorsWho')} />

      <form
        className="mt-5 space-y-4"
        onSubmit={e => { e.preventDefault(); if (dirty) save.mutate(); }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{t('common.name')}</span>
            <input
              className="input mt-1"
              value={form.name}
              onChange={e => set('name', e.target.value)}
              maxLength={150}
              disabled={save.isPending}
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{t('home.accountSettings.email')}</span>
            <input
              type="email"
              className="input mt-1"
              value={form.email}
              onChange={e => set('email', e.target.value)}
              disabled={save.isPending}
            />
          </label>
        </div>

        <dl className="grid gap-4 rounded-xl bg-slate-50 p-4 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{t('home.accountSettings.phone')}</dt>
            <dd className="mt-0.5 font-mono text-sm text-slate-800">{user?.phone || '—'}</dd>
            <dd className="mt-1 text-xs text-slate-500">{t('home.accountSettings.yourPhoneIsYourNacisIdentity')}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{t('home.accountSettings.memberSince')}</dt>
            <dd className="mt-0.5 text-sm text-slate-800">
              {user?.created_at ? fmtDateTime(user.created_at) : '—'}
            </dd>
          </div>
        </dl>

        <div className="flex items-center gap-3">
          <button type="submit" className="btn-primary" disabled={!dirty || save.isPending}>
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}{t('home.accountSettings.saveChanges')}</button>
          {!dirty && <span className="text-xs text-slate-500">{t('home.accountSettings.noUnsavedChanges')}</span>}
        </div>
      </form>
    </Card>
  );
}

// ── Privacy mode ─────────────────────────────────────────────────────────────

const MODES = (t) => [
  {
    value: 'identified',
    label: t('home.accountSettings.modesIdentified'),
    icon: Eye,
    blurb: 'Reports you file carry your identity. Agencies can contact you for follow-up, '
         + 'and your submissions carry more weight in the Priority Index.',
  },
  {
    value: 'anonymous',
    label: t('home.accountSettings.modesAnonymous'),
    icon: EyeOff,
    blurb: 'Reports you file are not attributed to you. Nobody will call you back, '
         + 'and an anonymous report is scored more conservatively.',
  },
];

function PrivacyModeCard({ user }) {
  const { t } = useTranslation();
  const { applyProfile } = useAuth();
  const current = user?.mode ?? 'identified';
  const [pending, setPending] = useState(null);

  const change = useMutation({
    mutationFn: (mode) => authApi.updatePrivacyMode(mode),
    onSuccess: (res) => {
      toast.success(`Privacy mode set to ${res?.mode ?? 'updated'}.`);
      applyProfile({ mode: res?.mode });
      setPending(null);
    },
    onError: err => {
      toast.error(err?.response?.data?.message || 'Could not change your privacy mode.');
      setPending(null);
    },
  });

  return (
    <Card>
      <CardHeader
        title={t('home.accountSettings.privacyMode')}
        description={t('home.accountSettings.governsWhetherTheReportsYouFile')}
      />

      <ul className="mt-5 grid gap-3 sm:grid-cols-2">
        {MODES(t).map(m => {
          const Icon = m.icon;
          const active = current === m.value;
          const busy = change.isPending && pending === m.value;
          return (
            <li key={m.value}>
              <button
                type="button"
                onClick={() => { if (!active) { setPending(m.value); change.mutate(m.value); } }}
                disabled={active || change.isPending}
                className={active
                  ? 'flex h-full w-full flex-col items-start gap-2 rounded-xl border-2 border-sky-500 bg-sky-50 p-4 text-left'
                  : 'flex h-full w-full flex-col items-start gap-2 rounded-xl border border-slate-200 p-4 text-left transition hover:border-slate-300 hover:bg-slate-50 disabled:opacity-60'}
              >
                <span className="flex w-full items-center justify-between gap-2">
                  <span className="flex items-center gap-2 font-semibold text-slate-900">
                    <Icon className="h-4 w-4" /> {m.label}
                  </span>
                  {busy
                    ? <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                    : active && <Check className="h-4 w-4 text-sky-700" />}
                </span>
                <span className="text-sm text-slate-600">{m.blurb}</span>
                {active && (
                  <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-sky-700">{t('home.accountSettings.current')}</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

// ── Roles + permissions ──────────────────────────────────────────────────────

function RolesCard({ roles }) {
  const { t } = useTranslation();
  // Fetched fresh rather than read from the cached session: a role granted or
  // withdrawn since sign-in would otherwise not show until the next login.
  const query = useQuery({
    queryKey: ['auth', 'permissions'],
    queryFn:  () => authApi.permissions(),
    retry: 1,
  });

  const liveRoles = query.data?.roles ?? roles ?? [];
  const perms     = query.data?.permissions ?? [];

  return (
    <Card>
      <CardHeader title={t('home.accountSettings.access')} description={t('home.accountSettings.whatYourAccountIsEntitledTo')} />

      <div className="mt-5 space-y-4">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{t('home.accountSettings.roles')}</h3>
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {liveRoles.length === 0
              ? <li className="text-sm text-slate-600">{t('home.accountSettings.noRolesAssigned')}</li>
              : liveRoles.map(r => (
                  <li
                    key={r}
                    className="rounded-full bg-slate-900 px-2.5 py-0.5 text-xs font-medium text-white"
                  >
                    {fmtRoleLabel(r)}
                  </li>
                ))}
          </ul>
        </div>

        <div>
          <h3 className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{t('home.accountSettings.permissions')} {perms.length > 0 && <span className="text-slate-400">({perms.length})</span>}
          </h3>
          {query.isLoading ? (
            <div className="mt-2 space-y-1.5">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-5 animate-pulse rounded bg-slate-100" />
              ))}
            </div>
          ) : query.isError ? (
            <div className="mt-2">
              <ErrorPanel error={query.error} onRetry={query.refetch} />
            </div>
          ) : perms.length === 0 ? (
            <p className="mt-2 text-sm text-slate-600">{t('home.accountSettings.noGranularPermissionsYourAccessDerives')}</p>
          ) : (
            <ul className="mt-2 max-h-56 space-y-1 overflow-y-auto pr-1">
              {perms.map(p => (
                <li key={p} className="font-mono text-xs text-slate-700">{p}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}

// ── Devices ──────────────────────────────────────────────────────────────────

function DevicesCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(null);

  const thisFingerprint = localStorage.getItem(DEVICE_FP_KEY);

  const query = useQuery({
    queryKey: ['auth', 'devices'],
    queryFn:  () => deviceApi.list(),
    retry: 1,
  });

  const revoke = useMutation({
    mutationFn: (id) => deviceApi.revoke(id),
    onSuccess: () => {
      toast.success('Device revoked.');
      qc.invalidateQueries({ queryKey: ['auth', 'devices'] });
      setConfirming(null);
    },
    onError: err => toast.error(err?.response?.data?.message || 'Could not revoke that device.'),
  });

  const devices = Array.isArray(query.data) ? query.data : (query.data?.data ?? []);

  return (
    <Card>
      <CardHeader
        title={t('home.accountSettings.devices')}
        description={t('home.accountSettings.devicesRegisteredToYourAccountReport')}
      />

      <div className="mt-5">
        {query.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100" />
            ))}
          </div>
        ) : query.isError ? (
          <ErrorPanel error={query.error} onRetry={query.refetch} />
        ) : devices.length === 0 ? (
          <EmptyState
            title={t('home.accountSettings.noDevicesRegistered')}
            description={t('home.accountSettings.thisBrowserRegistersItselfWhenYou')}
          />
        ) : (
          <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
            {devices.map(d => {
              // The API projection omits the fingerprint, so "this device" is
              // inferred from platform + a live registration. It is a hint for
              // the confirmation copy, not an authorisation decision.
              const likelyThis = d.platform === 'web' && Boolean(thisFingerprint);
              const revoked = d.trust_level === 'revoked';
              return (
                <li key={d.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Smartphone className="h-4 w-4 flex-shrink-0 text-slate-400" />
                      <span className="font-medium capitalize text-slate-900">
                        {d.device_model || d.platform || 'Device'}
                      </span>
                      {likelyThis && !revoked && (
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-600">{t('home.accountSettings.thisBrowser')}</span>
                      )}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {d.platform}
                      {d.last_attested_at && <> {t('home.accountSettings.lastSeen')} {fmtRelative(d.last_attested_at)}</>}
                    </div>
                  </div>

                  <div className="flex flex-shrink-0 items-center gap-3">
                    <StatusBadge status={d.trust_level} />
                    {!revoked && (
                      <button
                        type="button"
                        onClick={() => setConfirming(d)}
                        className="text-xs font-semibold text-rose-700 transition hover:text-rose-900"
                      >{t('common.revoke')}</button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {confirming && (
        <div className="mt-4 space-y-3 rounded-xl border border-rose-200 bg-rose-50 p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-700" />
            <div className="text-sm text-rose-900">
              <p className="font-semibold">{t('common.revoke')} {confirming.device_model || confirming.platform || 'this device'}?
              </p>
              <p className="mt-1">
                {confirming.platform === 'web'
                  ? 'If this is the browser you are using, you will lose access to report submission '
                    + 'and the consent pages until you sign in again and it re-registers.'
                  : 'That device will no longer be able to submit reports or manage consent. '
                    + 'Revocation is permanent — the device must re-attest to return.'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-danger"
              onClick={() => revoke.mutate(confirming.id)}
              disabled={revoke.isPending}
            >
              {revoke.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}{t('home.accountSettings.revokeDevice')}</button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setConfirming(null)}
              disabled={revoke.isPending}
            >{t('common.cancel')}</button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ── Sessions ─────────────────────────────────────────────────────────────────

function SessionsCard() {
  const { t } = useTranslation();
  const { refreshSession, logoutAllSessions } = useAuth();
  const [confirmingAll, setConfirmingAll] = useState(false);

  const rotate = useMutation({
    mutationFn: () => refreshSession(),
    onSuccess: () => toast.success('Session extended. Your previous token has been revoked.'),
    onError: err => toast.error(err?.response?.data?.message || 'Could not extend your session.'),
  });

  const revokeAll = useMutation({
    mutationFn: () => logoutAllSessions(),
    onSuccess: () => {
      toast.success('All sessions revoked. Please sign in again.');
      // logoutAllSessions has already cleared local state; a hard navigation
      // guarantees no stale query cache survives into the login screen.
      window.location.assign('/login?reason=sessions_revoked');
    },
    onError: err => toast.error(err?.response?.data?.message || 'Could not revoke your sessions.'),
  });

  return (
    <Card>
      <CardHeader title={t('home.accountSettings.sessions')} description={t('home.accountSettings.tokensIssuedToYourAccountAcross')} />

      <div className="mt-5 space-y-4">
        <div className="rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-slate-600" />
            <h3 className="text-sm font-semibold text-slate-900">{t('home.accountSettings.extendThisSession')}</h3>
          </div>
          <p className="mt-1 text-sm text-slate-600">{t('home.accountSettings.issuesAFresh30DayToken')}</p>
          <button
            type="button"
            className="btn-secondary mt-3 w-full"
            onClick={() => rotate.mutate()}
            disabled={rotate.isPending}
          >
            {rotate.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}{t('home.accountSettings.rotateToken')}</button>
        </div>

        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-rose-700" />
            <h3 className="text-sm font-semibold text-rose-900">{t('home.accountSettings.signOutEverywhere')}</h3>
          </div>
          <p className="mt-1 text-sm text-rose-900">{t('home.accountSettings.revokesEveryTokenOnYourAccount')}</p>

          {confirmingAll ? (
            <div className="mt-3 space-y-2">
              <p className="text-sm font-semibold text-rose-900">{t('home.accountSettings.thisSignsYouOutHereToo')}</p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="btn-danger"
                  onClick={() => revokeAll.mutate()}
                  disabled={revokeAll.isPending}
                >
                  {revokeAll.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}{t('home.accountSettings.revokeAll')}</button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setConfirmingAll(false)}
                  disabled={revokeAll.isPending}
                >{t('common.cancel')}</button>
              </div>
            </div>
          ) : (
            <button type="button" className="btn-danger mt-3 w-full" onClick={() => setConfirmingAll(true)}>
              <LogOut className="h-4 w-4" /> {t('home.accountSettings.signOutEverywhere')}</button>
          )}
        </div>
      </div>
    </Card>
  );
}
